import { useMemo } from 'react';
import { getDate, addMinutes } from 'date-fns';
// import { getPositionAndWidth } from '@/lib/scheduling/positioning-compatibility-layer';

// Temporary placeholder function until positioning-compatibility-layer is implemented
const getPositionAndWidth = (...args: any[]) => ({ left: 0, width: 100 });
import { Tables } from '@/integrations/supabase/types';

type Event = Tables<'events'>;
type EventAssignment = Tables<'event_assignments'>;

interface EventWithAssignments extends Event {
  event_assignments: (EventAssignment & {
    employees: {
      name: string;
      email: string;
    } | null;
  })[];
}

interface EventPosition {
  left: number;
  width: number;
  startTime: Date;
  endTime: Date;
  row: number;
  totalRows: number;
}

interface UseEventPositioningProps {
  events?: EventWithAssignments[];
  showEvents?: boolean;
  daysInSelectedMonth: Date[];
  effectiveDisplayStartTime: Date;
  effectiveDisplayEndTime: Date;
  totalDisplayHours: number;
  hourCellWidthPx: number;
}

import { INTERVAL_MINUTES } from '@/lib/core/time-constants';
const MINUTES_PER_HOUR = 60;

export const useEventPositioning = ({
  events,
  showEvents,
  daysInSelectedMonth,
  effectiveDisplayStartTime,
  effectiveDisplayEndTime,
  totalDisplayHours,
  hourCellWidthPx
}: UseEventPositioningProps) => {
  // Organize events by day
  const eventsByDay = useMemo(() => {
    if (!events || !showEvents) return {};
    
    const eventsMap: { [key: number]: EventWithAssignments[] } = {};
    const visibleDays = new Set(daysInSelectedMonth.map(day => getDate(day)));

    events.forEach(event => {
      const eventDate = new Date(event.event_date);
      const eventDay = getDate(eventDate);
      
      if (!visibleDays.has(eventDay)) {
        return;
      }
      
      if (!eventsMap[eventDay]) {
        eventsMap[eventDay] = [];
      }
      
      eventsMap[eventDay].push(event);
    });
    
    return eventsMap;
  }, [events, showEvents, daysInSelectedMonth]);

  // Smart event positioning with overlap detection
  const eventPositionsWithLayout = useMemo(() => {
    if (!showEvents || !events) return {};
    
    const positionsByDay: { [day: number]: { [eventId: string]: EventPosition } } = {};
    
    // Process events by day for smart positioning
    Object.entries(eventsByDay).forEach(([dayString, dayEvents]) => {
      const day = parseInt(dayString);
      positionsByDay[day] = {};
      
      // Calculate basic positions first
      const eventData = dayEvents.map(event => {
        const eventStartTime = new Date(`${event.event_date}T${event.start_time}`);
        const eventEndTime = addMinutes(eventStartTime, event.duration_minutes);
        
        const { left, width } = getPositionAndWidth(
          eventStartTime, 
          eventEndTime, 
          effectiveDisplayStartTime, 
          effectiveDisplayEndTime, 
          totalDisplayHours, 
          INTERVAL_MINUTES, 
          MINUTES_PER_HOUR, 
          hourCellWidthPx
        );
        
        return {
          event,
          left,
          width: Math.max(width, 80),
          startTime: eventStartTime,
          endTime: eventEndTime,
          startMinutes: eventStartTime.getHours() * 60 + eventStartTime.getMinutes(),
          endMinutes: eventEndTime.getHours() * 60 + eventEndTime.getMinutes()
        };
      });
      
      // Sort events by start time
      eventData.sort((a, b) => a.startMinutes - b.startMinutes);
      
      // Detect overlaps and assign rows
      const rows: Array<{ endMinutes: number; events: typeof eventData }> = [];
      
      eventData.forEach(eventInfo => {
        // Find the first row where this event can fit
        let assignedRow = 0;
        let foundRow = false;
        
        for (let i = 0; i < rows.length; i++) {
          const lastEventInRow = rows[i].events[rows[i].events.length - 1];
          if (!lastEventInRow || lastEventInRow.endMinutes <= eventInfo.startMinutes) {
            // No overlap, can use this row
            rows[i].events.push(eventInfo);
            rows[i].endMinutes = eventInfo.endMinutes;
            assignedRow = i;
            foundRow = true;
            break;
          }
        }
        
        if (!foundRow) {
          // Create new row
          assignedRow = rows.length;
          rows.push({
            endMinutes: eventInfo.endMinutes,
            events: [eventInfo]
          });
        }
        
        // Store position data
        positionsByDay[day][eventInfo.event.id] = {
          left: eventInfo.left,
          width: eventInfo.width,
          startTime: eventInfo.startTime,
          endTime: eventInfo.endTime,
          row: assignedRow,
          totalRows: Math.max(rows.length, 1)
        };
      });
    });
    
    return positionsByDay;
  }, [eventsByDay, showEvents, effectiveDisplayStartTime, effectiveDisplayEndTime, totalDisplayHours, hourCellWidthPx]);

  // Create event lookup map for performance
  const eventsLookup = useMemo(() => {
    if (!events) return new Map();
    return new Map(events.map(event => [event.id, event]));
  }, [events]);

  return {
    eventsByDay,
    eventPositionsWithLayout,
    eventsLookup
  };
};